import type { ReadonlyJSONValue } from "@rocicorp/zero";
import { useEffect } from "react";
import type {
  ChatTool,
  ToolResult,
  ToolUseProps,
} from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";

export type ExecutionOutputView = {
  executionId: string;
  fileName: string;
  mediaType: string;
  sizeBytes: number;
};

type Data = {
  respondingAt: number | null;
  onShowOutput: (output: ExecutionOutputView) => void;
};

function object(value: ReadonlyJSONValue | null | undefined) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, ReadonlyJSONValue>)
    : null;
}

function string(value: ReadonlyJSONValue | undefined) {
  return typeof value === "string" ? value : null;
}

function number(value: ReadonlyJSONValue | undefined) {
  return typeof value === "number" ? value : null;
}

function viewFromResult(result: ToolResult | null): ExecutionOutputView | null {
  const output = object(result?.output);
  const executionId = string(output?.executionId);
  const fileName = string(output?.fileName);
  const mediaType = string(output?.mediaType);
  const sizeBytes = number(output?.sizeBytes);
  return executionId && fileName && mediaType && sizeBytes != null
    ? { executionId, fileName, mediaType, sizeBytes }
    : null;
}

function ShowExecutionOutputHeader({
  toolUse,
  result,
  data,
  createdAt,
  interrupted,
}: ToolUseProps) {
  const input = object(toolUse.input);
  const fileName = string(input?.fileName);
  const done = !!result && !result.isError;
  const toolData = data as Data | undefined;
  const respondingAt = toolData?.respondingAt ?? null;
  const onShowOutput = toolData?.onShowOutput;
  const view = viewFromResult(result);
  const executionId = view?.executionId ?? null;
  const outputFileName = view?.fileName ?? null;
  const mediaType = view?.mediaType ?? null;
  const sizeBytes = view?.sizeBytes ?? null;

  useEffect(() => {
    if (
      executionId &&
      outputFileName &&
      mediaType &&
      sizeBytes != null &&
      respondingAt != null &&
      createdAt >= respondingAt
    ) {
      onShowOutput?.({
        executionId,
        fileName: outputFileName,
        mediaType,
        sizeBytes,
      });
    }
  }, [
    executionId,
    outputFileName,
    mediaType,
    sizeBytes,
    respondingAt,
    createdAt,
    onShowOutput,
  ]);

  return (
    <ToolStatus
      icon="output"
      text={
        done
          ? fileName
            ? `Showed ${fileName}`
            : "Showed output"
          : fileName
            ? `Opening ${fileName}…`
            : "Opening output…"
      }
      pending={!result && !interrupted}
    />
  );
}

export const showExecutionOutput = (data: Data): ChatTool => ({
  name: "showExecutionOutput",
  Header: ShowExecutionOutputHeader,
  alwaysLoadOutput: true,
  onActivate: (props) => {
    const view = viewFromResult(props.result);
    if (view) data.onShowOutput(view);
  },
  data,
});
